import React, { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

interface SaveViewConfigDialogProps {
  show: boolean
  onHide: () => void
  onSave: (config: SaveViewConfig) => void
  defaultName?: string
  error?: {
    name?: string
    filter_rules?: string
  }
}

export interface SaveViewConfig {
  name: string
  showInSideBar: boolean
  showOnDashboard: boolean
}

export const SaveViewConfigDialog: React.FC<SaveViewConfigDialogProps> = ({ show, onHide, onSave, defaultName = '', error }) => {
  const { t } = useTranslation()
  const [name, setName] = useState(defaultName)
  const [showInSideBar, setShowInSideBar] = useState(false)
  const [showOnDashboard, setShowOnDashboard] = useState(false)
  const [closeEnabled, setCloseEnabled] = useState(false)

  useEffect(() => {
    if (show) {
      setName(defaultName)
      setShowInSideBar(false)
      setShowOnDashboard(false)
      // Wait to enable close button so it doesn't steal focus from input
      setTimeout(() => {
        setCloseEnabled(true)
      }, 100)
    }
  }, [show, defaultName])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      name,
      showInSideBar,
      showOnDashboard,
    })
  }

  const handleCancel = () => {
    setCloseEnabled(false)
    onHide()
  }

  return (
    <Modal show={show} onHide={handleCancel} backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={closeEnabled}>
          <Modal.Title>{t('save_current_view') || 'Sačuvaj trenutni pregled'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('name') || 'Naziv'}</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              isInvalid={!!error?.name}
              required
              autoFocus
            />
            {error?.name && <Form.Control.Feedback type="invalid">{error.name}</Form.Control.Feedback>}
          </Form.Group>

          <Form.Check
            type="checkbox"
            id="showInSideBar"
            label={t('show_in_sidebar') || 'Prikaži u bočnoj traci'}
            checked={showInSideBar}
            onChange={(e) => setShowInSideBar(e.target.checked)}
            className="mb-2"
          />

          <Form.Check
            type="checkbox"
            id="showOnDashboard"
            label={t('show_on_dashboard') || 'Prikaži na kontrolnoj tabli'}
            checked={showOnDashboard}
            onChange={(e) => setShowOnDashboard(e.target.checked)}
          />

          {error?.filter_rules && typeof error.filter_rules === 'string' && (
            <div className="alert alert-danger mt-3" role="alert">
              <h6 className="alert-heading">{t('filter_rules_error') || 'Greška u pravilima filtera prilikom čuvanja pogleda'}</h6>
              {t('error_returned') || 'Vraćena greška'}:<br />
              {error.filter_rules}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCancel}>
            {t('cancel') || 'Otkaži'}
          </Button>
          <Button variant="primary" type="submit">
            {t('save') || 'Sačuvaj'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default SaveViewConfigDialog
